































































































/* General */
import Vue from 'vue'
import DefaultLayout from '@/layouts/default-layout.vue'
import MainContainer from '@/layouts/main-container.vue'
import { cloneDeep, debounce } from 'lodash'

/* Mixins */

/* Components */
import SleekFilterInput from '@/components/global/inputs/sleek-filter-input.vue'
import PeopleRoster from './components/people-roster.vue'

/* Helpers */

import RouteAssistant from '@/helpers/route-assistant'
import AppSumoAssistant from '@/helpers/appsumo-assistant'

/* Data types */
import { ICompanyRoster, IItemForSelection } from '@/api/types/response.types'

/* API */
import CompaniesAPI from '@/api/modules/companies.api'

export default Vue.extend({
  name: 'PeopleHome',

  metaInfo: {
    title: 'Users',
  },

  components: {
    DefaultLayout,
    MainContainer,
    SleekFilterInput,
    PeopleRoster,
  },

  data() {
    return {
      AppSumoAssistant,
      isLoading: true,
      isSendingBulkInvitations: false,
      bulkInvitationsAttempted: false,
      unregisteredUsersCount: 0,
      debounceFilterCompanyRosters: null,
      RouteAssistant,
      searchQuery: '',
      companyRosters: [] as ICompanyRoster[],
      filteredCompanyRosters: [] as ICompanyRoster[],
    }
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    companies(): IItemForSelection[] {
      return this.companyRosters.map(c => ({
        id: c.id,
        name: c.name,
      }))
    },
    noFilterMatches(): boolean {
      // We will never have a case where an acct has no companies,
      // so if filtered companies length is 0, we know it's due to no filter matches.
      return this.filteredCompanyRosters.length === 0
    },
    hasSearchQuery(): boolean {
      return this.searchQuery.length > 0
    },
    hasMultipleUnregisteredUsers(): boolean {
      return this.unregisteredUsersCount > 1
    },
  },

  watch: {
    searchQuery: {
      immediate: false,
      handler() {
        this.debounceFilterCompanyRosters()
      },
    },
  },

  async created() {
    this.debounceFilterCompanyRosters = debounce(this.filterCompanyRosters, 300)
    this.isLoading = true
    await this.fetchCompanyRosters()
    this.isLoading = false
  },

  methods: {
    noPeopleInCompany(company: ICompanyRoster): boolean {
      return company.numberOfPeople === 0
    },
    filterCompanyRosters(): void {
      if (!this.hasSearchQuery) {
        this.filteredCompanyRosters = this.companyRosters
        return
      }
      this.filteredCompanyRosters = []
      this.companyRosters.forEach(company => {
        // Company match...
        if (
          company.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        ) {
          this.filteredCompanyRosters.push(company)
        } else {
          // People match...
          const peopleMatches = company.listPeople.filter(person => {
            return (
              person.name
                .toLowerCase()
                .includes(this.searchQuery.toLowerCase()) ||
              person.email
                .toLowerCase()
                .includes(this.searchQuery.toLowerCase())
            )
          })

          const curCompany: ICompanyRoster = cloneDeep(company)
          curCompany.listPeople = peopleMatches

          if (peopleMatches.length > 0) {
            this.filteredCompanyRosters.push(curCompany)
          }
        }
      })
    },
    async goToCreateCompany() {
      if (
        !(await AppSumoAssistant.isUnderAccountMembershipsLimit(
          this.$router,
          this.$permissions,
        ))
      ) {
        return
      }

      RouteAssistant.goToCreateCompany(this.$router, this.accountID)
    },
    async goToCreatePeople() {
      if (
        !(await AppSumoAssistant.isUnderAccountMembershipsLimit(
          this.$router,
          this.$permissions,
        ))
      ) {
        return
      }

      RouteAssistant.goToCreatePeople(this.$router, this.accountID)
    },
    async fetchCompanyRosters() {
      try {
        this.companyRosters = await CompaniesAPI.get_Rosters()
        this.unregisteredUsersCount = this.companyRosters
          .map(company => company.listPeople) // Extract all people arrays
          .reduce((acc, people) => acc.concat(people), []) // Flatten the array of people
          .filter(person => !person.hasRegistered).length // Filter people with hasRegistered == true

        this.filterCompanyRosters()
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async deleteCompany(id: number) {
      try {
        const response = await CompaniesAPI.delete_Delete(id)
        this.$messaging.showSuccess(response.message)
        this.isLoading = true
        await this.fetchCompanyRosters()
        this.isLoading = false
      } catch (error) {
        this.$error.emit(error)
      }
    },
    handleInviteUnregisteredUsers() {
      this.$confirm(
        `They'll receive an email with a link and instructions to complete their registration.`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Send invites',
          confirmButtonClass: 'el-button--primary',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: `Send invites to ${
            this.unregisteredUsersCount
          } unregistered users?`,
        },
      ).then(() => {
        this.inviteUnregisteredUsers()
      })
    },
    async inviteUnregisteredUsers() {
      try {
        this.isSendingBulkInvitations = true
        const response = await CompaniesAPI.get_BulkRegistrationInvitation()
        this.$messaging.showSuccess(response.message)
        this.isSendingBulkInvitations = false
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.bulkInvitationsAttempted = true
      }
    },
  },
})
